import React from 'react';
import { Alert, Typography } from '@duxton-web/ui';

import { getCountryNameByCountryCode } from '@/common/CountryCodesMap';
import { ModifiedRolloutTargetMapValue } from '@/common/dto/changelog';
import Service from '@/lib/App';
import {
  analyzeRolloutImpact,
  isActuallyGlobal,
} from '@/utils/rolloutImpactAnalysis';

interface RolloutImpactSummaryProps {
  rolloutTarget: Map<string, ModifiedRolloutTargetMapValue>;
  style?: React.CSSProperties;
}

function isOperationalCountry(countryCode: string): boolean {
  const countriesMap = Service.cache?.getCountriesMap();
  if (!countriesMap) return false;
  return typeof countriesMap[countryCode] === 'string';
}

export function getCountryImpactText(countryCodes: string[]): string {
  const countryNames = countryCodes.map((code) =>
    getCountryNameByCountryCode(code),
  );

  const operationalCountries: string[] = [];
  const operationalNames: string[] = [];
  const nonOperationalCountries: string[] = [];
  const nonOperationalNames: string[] = [];

  countryCodes.forEach((code, index) => {
    if (isOperationalCountry(code.toUpperCase())) {
      operationalCountries.push(code);
      operationalNames.push(countryNames[index] || code);
    } else {
      nonOperationalCountries.push(code);
      nonOperationalNames.push(countryNames[index] || code);
    }
  });

  if (operationalNames.length === 0 && nonOperationalNames.length > 0) {
    if (nonOperationalNames.length === 1) {
      return `country ${nonOperationalNames[0]}`;
    }

    const shown = nonOperationalNames.slice(0, 3);

    const remaining = nonOperationalNames.length - shown.length;
    let othersText = '';
    if (remaining > 0) {
      othersText = ` and ${remaining} other${remaining === 1 ? '' : 's'}`;
    }
    return `countries in ${shown.join(', ')}${othersText}`;
  }

  const displayParts: string[] = [];
  if (operationalNames.length > 0) {
    displayParts.push(operationalNames.join(', '));
  }
  if (nonOperationalNames.length > 0) {
    const othersText = ` ${nonOperationalNames.length} other${nonOperationalNames.length === 1 ? '' : 's'}`;
    displayParts.push(othersText);
  }

  // Generate final message
  const totalCountries = countryCodes.length;
  if (totalCountries === 1) {
    return `country ${displayParts[0]}`;
  }
  const displayText = displayParts.join(' and ');
  return `countries in ${displayText}`;
}

const RolloutImpactSummary = ({
  rolloutTarget,
  style,
}: RolloutImpactSummaryProps) => {
  const impact = analyzeRolloutImpact(rolloutTarget);

  const isGlobalChange =
    impact.isGlobal ||
    impact.countries.length === 0 ||
    isActuallyGlobal(impact.countries);

  const renderMessage = () => {
    if (isGlobalChange) {
      return (
        <Typography.Text style={{ lineHeight: '1.5' }}>
          Please review carefully. This rollout changes potentially impact the
          whole world!
        </Typography.Text>
      );
    }

    return (
      <Typography.Text style={{ lineHeight: '1.5' }}>
        Potentially impacts{' '}
        <Typography.Text strong>
          {getCountryImpactText(impact.countries)}
        </Typography.Text>
      </Typography.Text>
    );
  };

  const getAlertType = () => {
    if (isGlobalChange) {
      return 'error' as const;
    }
    return 'warning' as const;
  };

  const getAlertMessage = () => {
    if (isGlobalChange) {
      return 'GLOBAL change detected';
    }
    return 'Country level impact detected';
  };

  return (
    <Alert
      type={getAlertType()}
      message={getAlertMessage()}
      description={renderMessage()}
      showIcon
      data-testid="rollout-impact-summary"
      style={{
        wordWrap: 'break-word',
        overflow: 'hidden',
        ...style,
      }}
    />
  );
};

export default RolloutImpactSummary;
